/**
 * What the agent pane is showing, and how a reported kind becomes one.
 *
 * ONLY TWO OF THESE ARE CHOOSABLE. `terminal-view` and `auto-detect` are what a tap
 * on the agent chip flips between; the rest are what auto-detect RESOLVES TO and are
 * never selected by hand. There used to be a long-press menu offering all six, which
 * made sense while auto-detect could not name every agent — it answered `codex` for
 * anything that was not Claude, so pinning a view was the only way to be right. With
 * the kind fixed at the source (server 1220e8c) the menu offered a way to be wrong
 * about a question the app can now answer, so the owner had it removed.
 *
 * `VIEW_MODES` therefore lists what a mode can BE, not what a user may pick — it is
 * the validator for a stored or URL-supplied value, and `viewForKind`'s range.
 */

export type ViewMode =
  | 'terminal-view'
  | 'auto-detect'
  | 'cursor-view'
  | 'codex-view'
  | 'claude-view'
  | 'agy-view'
  | 'grok-view'
  | 'hermes-view'
  | 'muse-view'

export const VIEW_MODES: ViewMode[] = [
  'terminal-view',
  'auto-detect',
  'cursor-view',
  'codex-view',
  'claude-view',
  'agy-view',
  'grok-view',
  'hermes-view',
  'muse-view',
]

/**
 * The view auto-detect resolves to, from the kind the idle poll reports.
 *
 * Extracted from a ternary chain in App.tsx so the rule can be asserted directly.
 * It is the only place a server-reported kind becomes a view, which matters because
 * the two vocabularies are NOT the same and never have been: the poll says
 * `claude-code`, the view is `claude-view`; the poll says `agy`, the product is
 * called Antigravity. Anything unrecognised falls back to the raw terminal, which is
 * always correct — a session running something we cannot name still has a pane.
 *
 * `agy` was unreachable when the chip shipped: the server resolved every non-Claude
 * kind through `"codex" if cli == "codex" else "cursor"`, so an Antigravity agent
 * arrived labelled codex and would have been rendered by the Codex parser. Fixed
 * server-side in 1220e8c; this map is only meaningful because that landed, and the
 * picker's removal rests on the same fix.
 */
export function viewForKind(kind: string | null | undefined): ViewMode {
  switch (kind) {
    case 'cursor':
      return 'cursor-view'
    case 'codex':
      return 'codex-view'
    case 'claude-code':
      return 'claude-view'
    case 'agy':
      return 'agy-view'
    case 'grok':
      return 'grok-view'
    case 'hermes':
      return 'hermes-view'
    case 'muse':
      return 'muse-view'
    default:
      return 'terminal-view'
  }
}

/**
 * A stored or URL-supplied view, narrowed to the two a user can choose.
 *
 * An explicit rich mode is MIGRATED to auto-detect rather than honoured. It could only
 * have been written by the long-press menu that no longer exists, and honouring one
 * now would be worse than ignoring it: a view pinned to `codex-view` before the kind
 * was fixed at its source would go on rendering an Antigravity agent through the Codex
 * parser — the defect that was repaired, preserved in a preference where nothing would
 * think to question it.
 *
 * Lives here rather than in App so the rule has one home. A copy in a test would be
 * the version that stays right while the real one drifts.
 */
export function asViewMode(v: string | null): ViewMode | null {
  if (!v || !(VIEW_MODES as string[]).includes(v)) return null
  return v === 'terminal-view' ? 'terminal-view' : 'auto-detect'
}

/**
 * Does this view stage text in the agent's input box before committing it?
 *
 * The two-stage submit exists for ONE reason: an agent CLI has its own input box, so
 * the first press types into it without Enter and you read your own sentence back
 * inside the terminal before it goes. Long prompts and phone typos are what it is for.
 *
 * That reason is a property of the VIEW, not of agents. In Clean View the agent's
 * input box is not on screen — the whole point of the view is that it is not — so the
 * first press bought a read-back nobody could read, and charged a second press for it.
 * Two presses, none of the benefit.
 *
 * So the terminal keeps two stages, where the box is visible and the pause is real,
 * and every rich view sends on one press.
 */
export function stagesBeforeSending(mode: ViewMode): boolean {
  return mode === 'terminal-view'
}


/**
 * The kind to render with, given what the poll says NOW and what it last said.
 *
 * `viewForKind` sends an unrecognised kind to the terminal, and that is right for a
 * plain shell — the idle poll names those (`screen`), so a session with no agent
 * still reports a kind. **`undefined` therefore does not mean "no agent". It means
 * the poll has no answer**: the entry is missing because the host is unreachable, or
 * because nothing has come back yet.
 *
 * Collapsing those two into `terminal-view` is what threw the owner out of Clean View
 * every time yy-mbp-home dropped, and back in when it returned. The view flapped for
 * a reason that was never about the view — and worse, the flap is to the ONE mode
 * with different keyboard semantics, so a reconnect could silently turn Enter from
 * "send" back into "newline" under someone's hands.
 *
 * So a remembered kind wins over "no answer", and only over "no answer". A kind that
 * actually CHANGES still takes effect on the next poll, which is what makes the view
 * follow an agent that exits or is replaced — the auto-redetect this must not break.
 */
export function stickyKind(
  polled: string | null | undefined,
  remembered: string | null | undefined,
): string | null | undefined {
  return polled ?? remembered
}
